import { useModal } from "../lib/useModal";
import { useEffect, useState } from "react";

import { useRemoveFinding, useSaveNote, useSaveScore } from "../api/hooks";
import type { Finding } from "../api/types";
import {
  fmtDate,
  fmtDateTime,
  fmtEUR,
  hostLabel,
  urgencyLabel,
  urgencyToken,
} from "../lib/format";

/**
 * The record one is deciding on.
 *
 * The two profiles ask different questions of an entry, so the overview is
 * not the same list of rows with blanks in it: a call is read for its
 * deadline and its money, a signal for its field, its maturity and who is
 * behind it.
 */
export function FindingDrawer({
  finding,
  onClose,
  onBack,
}: {
  finding: Finding | null;
  onClose: () => void;
  /** Set when the entry was opened out of a cluster: the way back. */
  onBack?: () => void;
}) {
  const saveNote = useSaveNote();
  const saveScore = useSaveScore();
  const remove = useRemoveFinding();
  const open = finding !== null;
  const modal = useModal(open, onClose);
  const isTech = finding?.type === "technology";

  const [note, setNote] = useState("");
  const [score, setScore] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setNote(finding?.user_note ?? "");
    setScore(finding?.relevance_score != null ? String(finding.relevance_score) : "");
    setConfirming(false);
  }, [finding?.id, finding?.user_note, finding?.relevance_score]);

  const source = finding?.url || finding?.citations[0]?.url || "";

  return (
    <>
      <div id="drawer-backdrop" className={open ? "open" : ""} onClick={onClose} />
      <div id="drawer" className={open ? "open" : ""} ref={modal} role="dialog" aria-modal={open ? true : undefined} aria-labelledby="drawer-title" tabIndex={-1} inert={!open}>
        {finding && (
          <>
            <div className="drawer-head">
              <div>
                {onBack && (
                  <button type="button" className="drawer-back" onClick={onBack}>
                    Zurück
                  </button>
                )}
                <h2 id="drawer-title">{finding.title}</h2>
                <div className="sub mono" style={{ color: "var(--text-muted)", fontSize: 11 }}>
                  {isTech
                    ? [finding.kind, finding.year].filter(Boolean).join(" ")
                    : finding.call_identifier ?? ""}
                </div>
              </div>
              <button className="drawer-close" aria-label="Details schließen" id="drawer-close" onClick={onClose}>
                ×
              </button>
            </div>

            <div className="drawer-section">
              <h3>Übersicht</h3>

              {isTech ? (
                <>
                  <LedgerRow label="Cluster" value={finding.cluster ?? "ohne Cluster"} />
                  <LedgerRow label="Art" value={finding.kind || "-"} />
                  {finding.year != null && (
                    <LedgerRow label="Jahr" value={String(finding.year)} />
                  )}
                  <LedgerRow label="Technologiefeld" value={finding.technology_field || "-"} />
                  {finding.maturity_trl != null && (
                    <LedgerRow label="Reifegrad" value={`TRL ${finding.maturity_trl}`} />
                  )}
                  {finding.key_actors.length > 0 && (
                    <LedgerRow label="Akteure" value={finding.key_actors.join(", ")} />
                  )}
                  {finding.organisation && (
                    <LedgerRow label="Herausgeber" value={finding.organisation} />
                  )}
                  {finding.topics.length > 0 && (
                    <LedgerRow label="Stichworte" value={finding.topics.join(", ")} />
                  )}
                  <LedgerRow
                    label="Erfasst"
                    value={finding.first_seen ? fmtDate(finding.first_seen) : "-"}
                  />
                </>
              ) : (
                <>
                  <LedgerRow label="Organisation" value={finding.organisation ?? "-"} />
                  <LedgerRow label="Programm" value={finding.programme ?? "-"} />
                  {finding.deadline && (
                    <div className="ledger-row">
                      <span className="label">Frist</span>
                      <span
                        className="val"
                        style={{ color: `var(${urgencyToken(finding.days_left)})` }}
                      >
                        {fmtDate(finding.deadline)} ({urgencyLabel(finding.days_left)})
                      </span>
                    </div>
                  )}
                  {finding.budget_total_eur != null && (
                    <LedgerRow
                      label="Fördersumme"
                      value={
                        fmtEUR(finding.budget_total_eur) +
                        (finding.funding_rate != null
                          ? ` · ${Math.round(finding.funding_rate * 100)}%`
                          : "")
                      }
                    />
                  )}
                  {finding.trl_min != null && (
                    <LedgerRow label="TRL" value={`${finding.trl_min}–${finding.trl_max}`} />
                  )}
                  <LedgerRow label="Länder" value={finding.countries.join(", ") || "-"} />
                </>
              )}

              {source && (
                <div className="ledger-row">
                  <span className="label">Quelle</span>
                  <span className="val">
                    <a
                      className="source-link"
                      href={source}
                      target="_blank"
                      rel="noreferrer"
                      title={source}
                    >
                      {hostLabel(source)} ↗
                    </a>
                  </span>
                </div>
              )}
            </div>

            <div className="drawer-section score-detail">
              <h3>{isTech ? "Score" : "Autoflug Score"}</h3>
              <div className="score-row">
                <label className="label" htmlFor="score-field">
                  Score
                </label>
                <input
                  id="score-field"
                  className="score-field"
                  type="number"
                  min={1}
                  max={100}
                  value={score}
                  onChange={(event) => setScore(event.target.value)}
                />
                <span className="score-scale">von 100</span>
                <button
                  className="btn secondary"
                  disabled={
                    saveScore.isPending ||
                    !score ||
                    Number(score) < 1 ||
                    Number(score) > 100 ||
                    Number(score) === finding.relevance_score
                  }
                  onClick={() =>
                    saveScore.mutate({ findingId: finding.id, score: Number(score) })
                  }
                >
                  Übernehmen
                </button>
              </div>
              <p className="score-rationale">
                {finding.relevance_score == null
                  ? "Bewertung ausstehend."
                  : finding.relevance_rationale || "Für diesen Score liegt noch keine Begründung vor."}
              </p>
            </div>

            <div className="drawer-section">
              <h3>{isTech ? "Worum es geht" : "Volltext"}</h3>
              <p style={{ fontSize: 12.5 }}>{finding.summary}</p>
            </div>

            <div className="drawer-section">
              <h3>Belege</h3>
              {finding.citations.map((citation, index) => (
                <div className="citation-card" key={index}>
                  <div>{citation.title}</div>
                  {citation.excerpt && (
                    <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
                      „{citation.excerpt}“
                    </div>
                  )}
                  <div className="meta">
                    <a
                      className="source-link"
                      href={citation.url}
                      target="_blank"
                      rel="noreferrer"
                      title={citation.url}
                    >
                      {hostLabel(citation.url)} ↗
                    </a>{" "}
                    · abgerufen {fmtDateTime(citation.retrieved_at)}
                  </div>
                </div>
              ))}
            </div>

            <div className="drawer-section">
              <h3>Eigene Notiz</h3>
              <textarea
                className="note-field"
                rows={4}
                value={note}
                placeholder="Notiz…"
                onChange={(event) => setNote(event.target.value)}
              />
              <div className="action-bar" style={{ justifyContent: "flex-start" }}>
                <button
                  className="btn secondary"
                  disabled={saveNote.isPending || note === (finding.user_note ?? "")}
                  onClick={() => saveNote.mutate({ findingId: finding.id, note })}
                >
                  Notiz speichern
                </button>
                {saveNote.isSuccess && note === (finding.user_note ?? "") && (
                  <span className="hint" style={{ margin: 0 }}>
                    Gespeichert.
                  </span>
                )}
              </div>
            </div>

            {/* Its own section: removing an entry is not a neighbour of
                grading one, and the border says so. */}
            <div className="drawer-section danger-zone">
              <h3>Danger Zone</h3>
              <div className="action-bar" style={{ justifyContent: "flex-start" }}>
                {confirming ? (
                  <>
                    <button
                      className="btn danger"
                      disabled={remove.isPending}
                      onClick={() => {
                        remove.mutate({ findingId: finding.id });
                        onClose();
                      }}
                    >
                      Wirklich entfernen
                    </button>
                    <button className="btn secondary" onClick={() => setConfirming(false)}>
                      Abbrechen
                    </button>
                  </>
                ) : (
                  <button className="btn danger" onClick={() => setConfirming(true)}>
                    Entfernen
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function LedgerRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="ledger-row">
      <span className="label">{label}</span>
      <span className="val">
        {value}
      </span>
    </div>
  );
}
